import React from 'react';
import { MarkdownRenderer } from '@/components/markdown';

interface StreamingMarkdownTextProps {
  content: string;
  isStreaming?: boolean;
  streamingSpeed?: number; // kept for backward compat — ignored
  className?: string;
  onStreamingComplete?: () => void;
  uniqueKey?: string;
  appendOnly?: boolean; // kept for backward compat — ignored
}

/**
 * StreamingMarkdownText Component
 *
 * Renders markdown content that updates in real-time as the `content` prop
 * changes. Content appears in blocks as it arrives from the SSE stream —
 * no artificial character-by-character delay.
 *
 * While `isStreaming` is true a blinking cursor CSS class is applied.
 *
 * @example
 * ```tsx
 * <StreamingMarkdownText
 *   content={accumulatedText}
 *   isStreaming={true}
 *   uniqueKey={messageId}
 * />
 * ```
 */
export const StreamingMarkdownText: React.FC<StreamingMarkdownTextProps> = ({
  content,
  isStreaming = false,
  className = '',
  uniqueKey,
}) => {
  return (
    <div
      key={uniqueKey}
      className={`streaming-markdown-container ${className}`}
    >
      <MarkdownRenderer
        content={content}
        isStreaming={isStreaming}
        className={`streaming-markdown-content ${
          isStreaming ? 'streaming-active' : 'streaming-complete'
        }`}
      />
    </div>
  );
};

export default StreamingMarkdownText;
